// The Urbanism module inside the DocFlow shell (Legea nr. 169/2026, Ord. MDLPA nr. 975/2026): its sidebar items and its pages
// (#/urbanism/...). The requests are registered in the Registru Intrări-Ieșiri; the module adds the dossier, the act and the registers.
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { navigate, useAuth } from '../store';
import { Icon } from '../ui/Icon';
import type { UTasks } from './types';
import { URB_ROLES } from './types';
import { Acorduri, Dossiers, FormsCatalog, NewRequest, Settings, Tasks, UrbMap } from './Pages';
import { Dossier } from './Dossier';

/** The user can open the module: one of the urbanism roles, the registry (desk intake) or the administrator. */
export function hasUrbanism(roles: string[]) { return roles.some(r => r === 'ADMIN' || r === 'REGISTRY' || URB_ROLES.includes(r)); }

export const URB_TITLES: Record<string, string> = { tasks: 'De făcut — urbanism', dossiers: 'Dosare de urbanism', dossier: 'Dosar de urbanism', new: 'Cerere nouă (ghișeu)', map: 'Harta dosarelor de urbanism',
  forms: 'Formulare — Ord. MDLPA nr. 975/2026', acorduri: 'Registrul acordurilor unice', settings: 'Setări urbanism' };

export function useUrbBadges(active: boolean) {
  const [b, setB] = useState({ tasks: 0, overdue: 0 });
  useEffect(() => {
    if (!active) return;
    const load = () => api.get<UTasks>('/api/urbanism/tasks').then(t => {
      const ids = new Set<number>();
      for (const k of ['unassigned', 'mine', 'review', 'sign', 'approval'] as const) for (const d of t[k]) ids.add(d.id);
      setB({ tasks: ids.size, overdue: t.soon.filter(d => d.overdue).length });
    }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener('urb:changed', load);
    return () => { clearInterval(t); window.removeEventListener('urb:changed', load); };
  }, [active]);
  return b;
}

type NavItemFn = (p: { icon: string; label: string; active: boolean; onClick: () => void; count?: number; alert?: boolean; title?: string }) => React.ReactElement;

export function UrbNav({ page, NavItem }: { page: string; NavItem: NavItemFn }) {
  const { isAdmin } = useAuth();
  const b = useUrbBadges(true);
  return <>
    <div className="nav-section">Lucru</div>
    <NavItem icon="inbox" label="De făcut" count={b.tasks} alert={b.overdue > 0} active={page === 'tasks'} onClick={() => navigate('/urbanism/tasks')} />
    <NavItem icon="folder" label="Dosare" active={page === 'dossiers' || page === 'dossier'} onClick={() => navigate('/urbanism/dossiers')} />
    <NavItem icon="plus" label="Cerere la ghișeu" active={page === 'new'} onClick={() => navigate('/urbanism/new')} />
    <NavItem icon="map" label="Hartă" active={page === 'map'} onClick={() => navigate('/urbanism/map')} />
    <div className="nav-section">Registre</div>
    <NavItem icon="book" label="Registre și rapoarte" active={page === 'reports'} onClick={() => navigate('/reports?m=URB')} />
    <NavItem icon="clipboard" label="Acorduri unice" active={page === 'acorduri'} onClick={() => navigate('/urbanism/acorduri')} />
    <NavItem icon="file" label="Formulare" active={page === 'forms'} onClick={() => navigate('/urbanism/forms')} />
    <NavItem icon="users" label="Delegări" active={page === 'delegations'} onClick={() => navigate('/delegations')} />
    {isAdmin && <><div className="nav-section">Configurare</div><NavItem icon="settings" label="Setări urbanism" active={page === 'settings'} onClick={() => navigate('/urbanism/settings')} /></>}
  </>;
}

export function UrbContent({ parts }: { parts: string[] }) {
  const page = parts[1] || 'tasks';
  const { isAdmin, user } = useAuth();
  if (!hasUrbanism(user!.roles)) return <div className="empty"><Icon name="lock" /><h3>Nu aveți acces la modulul Urbanism</h3><p>Administratorul vă poate da unul dintre rolurile de urbanism din Administrare → Utilizatori.</p></div>;
  return <>
    {page === 'tasks' && <Tasks />}
    {page === 'dossiers' && <Dossiers />}
    {page === 'dossier' && parts[2] && <Dossier key={parts[2]} id={Number(parts[2])} />}
    {page === 'new' && <NewRequest initial={parts[2]} />}
    {page === 'map' && <UrbMap />}
    {page === 'forms' && <FormsCatalog />}
    {page === 'acorduri' && <Acorduri />}
    {page === 'settings' && isAdmin && <Settings />}
  </>;
}
